import { RefObject, useState } from 'react'

import {
  AppId,
  CrossAppDndAction,
  isHostPointerOverDesktopCrossAppDropSurface,
  useCrossAppCompletionListener,
  useCrossAppPointerListener
} from '@inno-tools/container-bridge'

type GridPosition = { row: number; col: number }

type UseDesktopCrossAppBridgeOptions = {
  gridRef: RefObject<HTMLDivElement | null>
  refreshFilesList: () => Promise<void>
  handleExternalPointerPreview: (args: {
    phase: 'start' | 'move' | 'end' | 'cancel'
    clientX: number
    clientY: number
    container: HTMLDivElement | null
    itemCount?: number
  }) => void
  computeExternalDropPositionFromPointer: (args: {
    clientX: number
    clientY: number
    container: HTMLDivElement | null
  }) => GridPosition | null
  getExternalFolderTargetAtPointer: (
    clientX: number,
    clientY: number,
    container: HTMLDivElement | null
  ) => string | null
  moveFilesItemsToDesktop: (
    itemIds: number[],
    dropPosition: GridPosition | null
  ) => Promise<void> | void
  moveFilesItemsIntoFolder: (
    folderItemId: string,
    itemIds: number[]
  ) => Promise<void> | void
}

export const useDesktopCrossAppBridge = ({
  gridRef,
  refreshFilesList,
  handleExternalPointerPreview,
  computeExternalDropPositionFromPointer,
  getExternalFolderTargetAtPointer,
  moveFilesItemsToDesktop,
  moveFilesItemsIntoFolder
}: UseDesktopCrossAppBridgeOptions) => {
  const [isBridgeDragOver, setIsBridgeDragOver] = useState(false)

  useCrossAppCompletionListener({
    // Accept only the two desktop-relevant move completions:
    // desktop<-files and desktop->external app.
    filter: payload =>
      (payload.sourceApp === 'container-app' &&
        payload.moverApp !== 'container-app' &&
        payload.itemIds.length > 0) ||
      (payload.sourceApp === AppId.CLUSTER_FILES &&
        payload.moverApp === 'container-app' &&
        payload.itemIds.length > 0),
    onCompletion: payload => {
      if (
        payload.sourceApp === AppId.CLUSTER_FILES &&
        payload.moverApp === 'container-app'
      ) {
        void refreshFilesList()
        return
      }

      void refreshFilesList()
    }
  })

  useCrossAppPointerListener({
    filter: payload =>
      payload.sourceApp === AppId.CLUSTER_FILES &&
      payload.action === CrossAppDndAction.MOVE,
    onPointer: payload => {
      const { phase, itemIds, clientX, clientY } = payload
      const pointerOverDesktop = isHostPointerOverDesktopCrossAppDropSurface(
        gridRef.current,
        clientX,
        clientY
      )
      const dropPosition = pointerOverDesktop
        ? computeExternalDropPositionFromPointer({
            clientX,
            clientY,
            container: gridRef.current
          })
        : null
      const isInsideDesktop = dropPosition !== null

      if (phase === 'start' || phase === 'move') {
        // Over a folder, let the folder's own highlight stand in for the
        // desktop-wide glow so the drop target reads unambiguously.
        const overFolder =
          getExternalFolderTargetAtPointer(
            clientX,
            clientY,
            gridRef.current
          ) !== null
        setIsBridgeDragOver(isInsideDesktop && !overFolder)
        handleExternalPointerPreview({
          phase,
          clientX,
          clientY,
          container: gridRef.current,
          itemCount: itemIds.length
        })
        return
      }

      setIsBridgeDragOver(false)
      handleExternalPointerPreview({
        phase,
        clientX,
        clientY,
        container: gridRef.current,
        itemCount: itemIds.length
      })

      if (phase === 'end' && itemIds.length > 0) {
        // A folder under the pointer wins: drop the files inside it. Otherwise
        // place them as shortcuts on the grid (only when over the desktop).
        const folderTargetId = getExternalFolderTargetAtPointer(
          clientX,
          clientY,
          gridRef.current
        )
        if (folderTargetId) {
          void moveFilesItemsIntoFolder(folderTargetId, itemIds)
        } else if (isInsideDesktop) {
          void moveFilesItemsToDesktop(itemIds, dropPosition)
        }
      }
    }
  })

  return { isBridgeDragOver }
}
